import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Lesson, LessonGroup, WeekDays } from 'src/app/components/lessons-schedule/lessons-schedule-list/types';
import { Room } from 'src/app/classes/Room';
import { User } from 'src/app/classes/User';
import { getRoomColorHex, getRoomColorTextHex } from 'src/app/util/room-color.util';

interface EditLessonPayload {
  lesson: Lesson;
  teacherId: number;
}

@Component({
  selector: 'app-schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrls: ['./schedule-calendar.component.scss'],
})
export class ScheduleCalendarComponent implements OnChanges {

  notes: { [key: string]: string[] } = {};
  WeekDays = WeekDays;

  @Input() lessonsSchedule: LessonGroup[];
  @Input() rawEvents: any;
  @Input() rooms: Room[] = [];
  @Input() teachers: User[];
  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() weekDates: Date[];
  @Output() editLesson = new EventEmitter<EditLessonPayload>();

  timeSlots: string[] = []; // ["10:00", "11:00", ...]
  table: {
    [slot: string]: {
      [id: string]: {
        [day: string]: any[];
      };
    };
  } = {};
  days: { date: Date; day: string }[] = [];
  today: Date = new Date();
  slots: any;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rawEvents && changes.rawEvents.currentValue) {
      this.buildSlotStructure();
    }
  }

  public formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  public getTeacherName(teacherId): string | boolean {
    if (!teacherId) {
      return false;
    }
    const teacher = this.teachers?.filter((t) => t.id === teacherId)[0];
    return `${teacher?.firstName || ''} ${teacher?.lastName || ''}`;
  }

  isStartOfWeek(date: Date): boolean {
    return date.getDay() === 0;
  }

  editLessonClick(lesson: Lesson): void {
    this.editLesson.emit({ lesson, teacherId: lesson.teacherId });
  }

  buildDaysFromPicker(start: Date, end: Date): void {
    const days: { date: Date; day: string }[] = [];
    const current = new Date(start);

    while (current <= end) {
      days.push({
        date: new Date(current),
        day: current.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
      });
      current.setDate(current.getDate() + 1);
    }

    this.days = days;
  }

  isDateInRange(event: any, date: Date, time: string): boolean {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);

    const dateInRange = date >= start && date <= end;

    const clonedDate = new Date(date);
    clonedDate.setHours(parseInt(time.slice(0, 2), 10), parseInt(time.slice(3, 5), 10), 0, 0);

    const pad = (n) => String(n).padStart(2, '0');

    const dateString =
      clonedDate.getFullYear() + '-' +
      pad(clonedDate.getMonth() + 1) + '-' +
      pad(clonedDate.getDate()) + 'T' +
      pad(clonedDate.getHours()) + ':' +
      pad(clonedDate.getMinutes()) + ':' +
      pad(clonedDate.getSeconds());

    const isLessonCancelled = event.cancelledDates.some(cancelledDate => cancelledDate === dateString);

    return dateInRange && !isLessonCancelled;
  }

  private buildSlotStructure(): any {
    this.buildDaysFromPicker(new Date(this.startDate), new Date(this.endDate));
    const slotMap = new Map<string, Map<number, any>>();

    for (const event of this.rawEvents) {
      const slotKey = `${event.startTime.slice(0, -3)} - ${event.endTime.slice(0, -3)}`;
      const roomKey: number = event.roomId !== null ? event.roomId : -1;

      if (!slotMap.has(slotKey)) {
        slotMap.set(slotKey, new Map());
      }

      const roomMap = slotMap.get(slotKey);

      if (!roomMap.has(roomKey)) {
        roomMap.set(roomKey, {
          id: roomKey,
          eventsByDay: {},
        });
      }

      const roomGroup = roomMap.get(roomKey);
      if (!roomGroup.eventsByDay[event.dayOfWeek]) {
        roomGroup.eventsByDay[event.dayOfWeek] = [];
      }

      roomGroup.eventsByDay[event.dayOfWeek].push(event);
    }

    this.slots = Array.from(slotMap.entries()).sort().map(([time, roomsMap]) => ({
      time,
      rooms: Array.from(roomsMap.values()),
    }));
  }

  getRoomColor(roomId: number): string {
    const room = this.rooms?.find((value) => value.id === roomId);
    return getRoomColorHex(room?.color);
  }

  getRoomTextColor(roomId: number): string {
    const room = this.rooms?.find((value) => value.id === roomId);
    return getRoomColorTextHex(room?.color);
  }
}
